import type { FunctionComponent, SVGProps, MouseEvent } from 'react'
import { useState } from 'react'
import { Badge } from 'components/Badge'
import type { BadgeStatus } from 'components/Badge'
import type { ToolTipFacing } from 'components/HotKeyToolTip'
import HotKeyToolTip from 'components/HotKeyToolTip'
import styled from 'styled-components'
import {
  contrastBackgroundColor,
  capsuleLightGreyColor,
  capsuleShadowColor,
  capsuleDarkBlue,
} from 'styles/styleVariables'

type ToolBarButtonProps = {
  label: string
  icon: FunctionComponent<SVGProps<SVGSVGElement>>
  disabled?: boolean
  badgeStatus?: BadgeStatus
  clickCallback: (event?: MouseEvent<HTMLElement>) => void
  tooltipText?: string
  tooltipPositioning?: {
    position: ToolTipFacing
    offsetTop: number
    offsetLeft: number
  }
  'data-testid'?: string
}

export const ToolBarButton = ({
  label,
  clickCallback,
  icon: Icon,
  disabled,
  tooltipText,
  tooltipPositioning = {
    position: 'left',
    offsetLeft: -1.5,
    offsetTop: 0,
  },
  'data-testid': testId,
  badgeStatus,
}: ToolBarButtonProps) => {
  const [showToolTip, setShowToolTip] = useState(false)

  return (
    <StyledCircleButton
      aria-label={tooltipText ?? label}
      data-testid={testId ?? label}
      onPointerEnter={() => setShowToolTip(true)}
      onPointerLeave={() => setShowToolTip(false)}
      onClick={clickCallback}
      disabled={disabled}
    >
      {!disabled && badgeStatus && <Badge status={badgeStatus} />}

      {tooltipText && <HotKeyToolTip forceShow={showToolTip} label={tooltipText} {...tooltipPositioning} />}

      <Icon stroke={!disabled ? capsuleDarkBlue : capsuleLightGreyColor} width={16} height={16} />
    </StyledCircleButton>
  )
}

const StyledCircleButton = styled.button<{
  disabled?: boolean
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3rem;
  width: 3rem;
  border-radius: 100%;
  background-color: ${contrastBackgroundColor};
  border: 2px solid ${contrastBackgroundColor};
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  will-change: transform;

  ${({ disabled }) =>
    disabled
      ? `
        cursor: not-allowed;
        background: none;
      `
      : `
        :hover {
          box-shadow: 0 1px 3px ${capsuleShadowColor};
        }
        :active {
          transform: translateY(1px);
          box-shadow: 0 1px 2px ${capsuleShadowColor};
        }
      `}
`
